<template>
  <!-- Flex row, never wraps (was a <b-container> which forced the items onto up to 3 lines). At narrow
  widths the text labels hide (CSS breakpoint below) leaving clickable icons; a tooltip carrying the
  hidden label is enabled only while collapsed (labelsCollapsed, driven by the same breakpoint via
  matchMedia). Under 550px the whole set of commands collapses into the hamburger dropdown instead.
  Roles/permissions gating is unchanged — only the presentation adapts. -->
  <div class="main-nav">
    <!-- Hamburger — only shown < 550px (CSS). Collapses every command below into a single menu; the
    same permission gating as the inline entries is repeated on each item. -->
    <b-nav-item-dropdown right no-caret class="nav-hamburger" @show="closeOpenPanels">
      <template #button-content>
        <span v-b-tooltip.hover.bottom :title="$t('navbar.menu')" class="nav-entry-inner">
          <svg
            class="nav-icon hamburger-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
          >
            <line x1="4" y1="7" x2="20" y2="7" />
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="17" x2="20" y2="17" />
          </svg>
        </span>
      </template>
      <b-dropdown-header>{{ $t('navbar.products') }}</b-dropdown-header>
      <b-dropdown-item class="b-d-item" to="/search">{{ $t('navbar.marketProducts') }}</b-dropdown-item>
      <b-dropdown-item class="b-d-item" to="/companyProducts">{{ $t(companyProductsLabelKey) }}</b-dropdown-item>
      <template v-if="$can('canViewScoringProfiles') || $can('canViewEfficiencyTest') || $can('canViewScoringProfileModels')">
        <b-dropdown-header>{{ $t('navbar.analisys') }}</b-dropdown-header>
        <b-dropdown-item v-if="$can('canViewScoringProfiles')" class="b-d-item" to="/scoringProfilesList">{{
          $t('navbar.scoringProfiles')
        }}</b-dropdown-item>
        <b-dropdown-item v-if="$can('canViewEfficiencyTest')" class="b-d-item" to="/efficiencyTestsList">{{
          $t('navbar.efficiencyTests')
        }}</b-dropdown-item>
        <b-dropdown-item v-if="$can('canViewScoringProfileModels')" class="b-d-item" to="/scoringProfilesModelsList">{{
          $t('navbar.modelsList')
        }}</b-dropdown-item>
      </template>
      <b-dropdown-divider></b-dropdown-divider>
      <b-dropdown-item
        v-if="$can('canViewScoringMonitoring')"
        class="b-d-item"
        to="/monitoringList"
        data-test="toMonitoringList"
        >{{ $t('navbar.monitoring') }}</b-dropdown-item
      >
      <b-dropdown-item class="b-d-item" to="/followed">{{ $t('navbar.favourites') }}</b-dropdown-item>
      <b-dropdown-item v-if="permissions.canViewDataChat" class="b-d-item" @click="openSidebarAi()">{{
        $t('navbar.aiAssistant')
      }}</b-dropdown-item>
      <b-dropdown-divider></b-dropdown-divider>
      <b-dropdown-item class="b-d-item" to="/configurations">{{ $t('navbar.configurations') }}</b-dropdown-item>
      <b-dropdown-item class="b-d-item" @click="openSidebar('download')">{{ $t('navbar.download') }}</b-dropdown-item>
      <b-dropdown-item class="b-d-item" @click="openSidebar('release')">{{ $t('navbar.releaseNotes') }}</b-dropdown-item>
      <!-- Asset class: the standalone selector is hidden < 550px, so its options live here as plain
      menu entries — a legend + the available classes by name only (no counters). -->
      <template v-if="hasMultipleAssetClasses">
        <b-dropdown-divider></b-dropdown-divider>
        <b-dropdown-header>{{ $t('navbar.assetClass.selectorTitle') }}</b-dropdown-header>
        <b-dropdown-item
          v-for="slug in availableAssetClasses"
          :key="slug"
          class="b-d-item"
          @click="selectAssetClass(slug)"
          >{{ assetClassName(slug) }}</b-dropdown-item
        >
      </template>
    </b-nav-item-dropdown>

    <b-nav-item-dropdown class="nav-entry nav-cmd sectionLink" no-caret @show="closeOpenPanels">
      <template #button-content>
        <span v-b-tooltip.hover.bottom :title="tip('navbar.products')" class="nav-entry-inner">
          <i class="fas fa-file-alt nav-icon"></i><span class="nav-label">{{ $t('navbar.products') }}</span>
        </span>
        <i class="fas fa-chevron-down small nav-caret"></i>
      </template>
      <b-dropdown-item class="b-d-item" to="/search">{{ $t('navbar.marketProducts') }}</b-dropdown-item>
      <b-dropdown-item class="b-d-item" to="/companyProducts">{{ $t(companyProductsLabelKey) }}</b-dropdown-item>
    </b-nav-item-dropdown>

    <b-nav-item-dropdown v-if="hasValidationItems" class="nav-entry nav-cmd sectionLink" no-caret @show="closeOpenPanels">
      <template #button-content>
        <span v-b-tooltip.hover.bottom :title="tip('navbar.validation')" class="nav-entry-inner">
          <i class="fas fa-tasks nav-icon"></i><span class="nav-label">{{ $t('navbar.validation') }}</span>
        </span>
        <i class="fas fa-chevron-down small nav-caret"></i>
      </template>
    </b-nav-item-dropdown>

    <div class="vl nav-cmd"></div>

    <b-nav-item-dropdown right no-caret class="nav-entry nav-cmd" @show="closeOpenPanels">
      <template #button-content>
        <span v-b-tooltip.hover.bottom :title="tip('navbar.analisys')" class="nav-entry-inner">
          <i class="fas fa-chart-bar nav-icon"></i><span class="nav-label">{{ $t('navbar.analisys') }}</span>
        </span>
        <i class="fas fa-chevron-down small nav-caret"></i>
      </template>

      <b-dropdown-item
        v-if="$can('canViewScoringProfiles')"
        class="b-d-item"
        to="/scoringProfilesList"
        data-test="toScoringProfiles"
        >{{ $t('navbar.scoringProfiles') }}</b-dropdown-item
      >
      <b-dropdown-item
        v-if="$can('canViewEfficiencyTest')"
        class="b-d-item"
        to="/efficiencyTestsList"
        data-test="toEfficiencyTestsList"
        >{{ $t('navbar.efficiencyTests') }}</b-dropdown-item
      >
      <b-dropdown-item
        v-if="$can('canViewScoringProfileModels')"
        class="b-d-item"
        to="/scoringProfilesModelsList"
        data-test="toScoringProfilesModelsList"
        >{{ $t('navbar.modelsList') }}</b-dropdown-item
      >
    </b-nav-item-dropdown>

    <b-nav-item
      v-if="$can('canViewScoringMonitoring')"
      :to="'/monitoringList'"
      class="nav-entry nav-cmd sectionLink"
      data-test="toMonitoringList"
    >
      <span v-b-tooltip.hover.bottom :title="tip('navbar.monitoring')" class="nav-entry-inner">
        <i class="far fa-file-search nav-icon"></i><span class="nav-label">{{ $t('navbar.monitoring') }}</span>
      </span>
    </b-nav-item>
    <b-nav-item :to="'/followed'" class="nav-entry nav-cmd sectionLink">
      <span v-b-tooltip.hover.bottom :title="tip('navbar.favourites')" class="nav-entry-inner">
        <i class="fas fa-star nav-icon"></i><span class="nav-label">{{ $t('navbar.favourites') }}</span>
      </span>
    </b-nav-item>

    <!-- AI button -->
    <b-nav-item v-if="permissions.canViewDataChat" :to="'#'" class="nav-entry nav-cmd sectionLink" @click="openSidebarAi()">
      <span v-b-tooltip.hover.bottom :title="tip('navbar.aiAssistant')" class="nav-entry-inner">
        <img class="brand-logo--ai nav-icon" :src="require('../../../assets/ai-icon-on.svg')" alt="logo" /><span
          class="nav-label"
          >{{ $t('navbar.aiAssistant') }}</span
        >
      </span>
    </b-nav-item>

    <asset-class-bar class="nav-entry nav-cmd" @on-close-open-panels="closeOpenPanels" />

    <div class="vl nav-cmd"></div>

    <b-nav-item :to="'/configurations'" class="nav-entry nav-cmd sectionLink--margin">
      <span v-b-tooltip :title="$t('navbar.configurations')">
        <i class="fas fa-cog nav-icon"></i>
      </span>
    </b-nav-item>
    <b-nav-item-dropdown class="nav-entry nav-cmd sectionLink--margin" no-caret @show="closeOpenPanels">
      <template #button-content>
        <span v-b-tooltip :title="$t('navbar.documentation')">
          <i class="fas fa-info-circle nav-icon"></i>
        </span>
      </template>
      <b-dropdown-item class="b-d-item" @click="openSidebar('download')">{{ $t('navbar.download') }}</b-dropdown-item>
      <b-dropdown-item class="b-d-item" @click="openSidebar('release')">{{
        $t('navbar.releaseNotes')
      }}</b-dropdown-item>
    </b-nav-item-dropdown>
    <b-nav-item class="nav-entry nav-cmd sectionLink--margin">
      <app-switcher />
    </b-nav-item>
  </div>
</template>

<script>
import AppSwitcher from '@/components/AppSwitcher/AppSwitcher.vue'
import AssetClassBar from './AssetClassBar.vue'
import { switchAssetClass } from '../../../utils/assetClassSwitch'
import { confirmDiscardUnsavedChanges } from '../../../utils/unsavedChanges'

// Single source of truth for the "hide the text labels" breakpoint — kept in sync with the
// `@media (max-width: …)` rule in this component's <style>. Below it the labels collapse to
// icon-only and the tooltips (which carry the hidden label) switch on.
const LABEL_COLLAPSE_QUERY = '(max-width: 1000px)'

export default {
  name: 'MainNavBar',
  components: {
    AppSwitcher,
    AssetClassBar,
  },
  props: {
    permissions: {
      type: Object,
      default: {},
    },
  },
  data() {
    return {
      labelsCollapsed: false,
      _labelMql: null,
    }
  },
  computed: {
    hasValidationItems() {
      return false
    },
    companyProductsLabelKey() {
      return this.$store.getters.getSelectedAssetClass === 'investment-certificates'
        ? 'navbar.companyProductsInvestmentCertificates'
        : 'navbar.companyProducts'
    },
    // Asset-class selection surfaced inside the hamburger (the standalone AssetClassBar is hidden
    // < 550px). Same store getters AssetClassBar uses.
    availableAssetClasses() {
      return this.$store.getters.getAvailableAssetClasses
    },
    selectedAssetClass() {
      return this.$store.getters.getSelectedAssetClass
    },
    hasMultipleAssetClasses() {
      return this.$store.getters.hasMultipleAssetClasses
    },
  },
  mounted() {
    // matchMedia (not a resize handler) reads exactly the same breakpoint the CSS uses, so the
    // tooltips turn on precisely when the labels are hidden — no width math, consistent with the
    // CSS-breakpoint approach.
    this._labelMql = window.matchMedia(LABEL_COLLAPSE_QUERY)
    this.labelsCollapsed = this._labelMql.matches
    this._onLabelMqlChange = (e) => {
      this.labelsCollapsed = e.matches
    }
    // addEventListener is the modern API; addListener is the fallback for older engines.
    if (this._labelMql.addEventListener) {
      this._labelMql.addEventListener('change', this._onLabelMqlChange)
    } else if (this._labelMql.addListener) {
      this._labelMql.addListener(this._onLabelMqlChange)
    }
  },
  beforeDestroy() {
    if (!this._labelMql) return
    if (this._labelMql.removeEventListener) {
      this._labelMql.removeEventListener('change', this._onLabelMqlChange)
    } else if (this._labelMql.removeListener) {
      this._labelMql.removeListener(this._onLabelMqlChange)
    }
  },
  methods: {
    // Tooltip title for a collapsible entry: the label while collapsed, empty otherwise (bootstrap-vue
    // shows no tooltip for an empty title), so the tooltip only appears once the text itself is gone.
    tip(key) {
      return this.labelsCollapsed ? this.$t(key) : ''
    },
    openSidebar(el) {
      this.$emit('open-sidebar', el)
    },
    closeOpenPanels() {
      this.$emit('on-close-open-panels')
    },
    openSidebarAi(el) {
      this.$emit('open-sidebar-ai', el)
    },
    assetClassName(slug) {
      return this.$t(`navbar.assetClass.${slug}.name`)
    },
    selectAssetClass(slug) {
      return confirmDiscardUnsavedChanges(this).then((proceed) => {
        if (proceed) {
          switchAssetClass({ store: this.$store, router: this.$router }, slug)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Single line, never wraps. min-width:0 lets the row shrink inside the flex navbar instead of
// pushing the right-hand nav (user/languages) off-screen.
.main-nav {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  min-width: 0;
}

// Every top-level entry aligns on the same centre line, so all dropdown menus open at the same
// height (they anchor to the bottom of equally-tall toggles).
.nav-entry {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

// Hamburger is hidden until the < 550px breakpoint swaps it in for the whole command set.
.nav-hamburger {
  display: none;
  align-items: center;
}

.nav-entry-inner {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.nav-icon {
  flex: 0 0 auto;
}

.nav-label {
  white-space: nowrap;
}

.nav-caret {
  margin-left: 0.35rem;
}

.vl {
  border-left: 1px solid white;
  height: 30px;
  margin: 0 0.5rem;
  opacity: 0.5;
  flex: 0 0 auto;
}

// Below the breakpoint: drop the text, keep the icons (tooltips carry the label). Kept in sync with
// LABEL_COLLAPSE_QUERY above. Also drop the dropdown carets and dividers to reclaim space in the
// worst case, so the result is a tidy row of icons.
@media (max-width: 1000px) {
  .main-nav .nav-label {
    display: none;
  }
  .main-nav .nav-caret {
    display: none;
  }
  .main-nav .vl {
    display: none;
  }
}

// Very narrow: collapse every command (asset-class selector included) into the hamburger menu — its
// options are re-rendered there as plain menu entries. Only the hamburger stays in this nav; the
// right-hand nav with user/language is untouched.
@media (max-width: 550px) {
  .main-nav .nav-cmd {
    display: none;
  }
  .main-nav .nav-hamburger {
    display: flex;
  }
}

.container--noborder {
  border: none !important;
  padding: 0 !important;
}

/deep/.b-d-item {
  margin-right: 0 !important;
}

/deep/.b-d-item a {
  padding: 10px 20px;
  color: black !important;
  font-size: 0.8rem;
}

/deep/.b-d-item a:hover {
  color: black !important;
}

/deep/.b-d-item a:active {
  background-color: #f8f9fa;
}

/deep/.products-menu:hover {
  color: #e2e2e2 !important;
}

img.brand-logo--ai {
  height: 20px !important;
}
</style>
